import { Loading } from "gsintegrations";
import React, { Suspense, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../auth/SubscriptionContext";
import { ROUTE } from "../constants/routes";
import { useCachedMedium } from "../hooks/useCachedMedium";
import { useCachedTerreiro } from "../hooks/useCachedTerreiro";
import { useHasPermission } from "../hooks/useHasPermission";
import { useMediumStatusQuery } from "../hooks/useMediumStatusQuery";
import { useMyPermissionsQuery } from "../hooks/usePermissionQueries";
import type { BooleanFeatureKey } from "../types/subscription.types";
import Forbidden from "../pages/Forbidden";
import PendingApprovalBlocked from "../pages/PendingApprovalBlocked";

type PermissionMode = "any" | "all";

interface PermissionProtectedPageProps {
  requiredPermissions: string | string[];
  mode?: PermissionMode;
  children: React.ReactNode;
  allowWithoutTerreiro?: boolean;
  allowWaitingApproval?: boolean;
  requiredFeature?: BooleanFeatureKey;
}

class ModuleErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch() {
    // Optionally log to monitoring / external service
  }

  handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" style={{ padding: 16 }}>
          <h2 style={{ marginTop: 0 }}>
            Algo deu errado ao carregar esta página
          </h2>
          <p style={{ color: "#666" }}>
            Houve um erro ao carregar o módulo desta rota. Tente recarregar a
            página.
          </p>
          <button onClick={this.handleRetry}>Recarregar</button>
          {!import.meta.env.PROD && this.state.error && (
            <pre style={{ marginTop: 12, whiteSpace: "pre-wrap" }}>
              {this.state.error.message}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}

const PlanFeatureBlocked: React.FC<{
  canViewPlans: boolean;
  unavailable?: boolean;
}> = ({ canViewPlans, unavailable = false }) => {
  const navigate = useNavigate();

  if (unavailable) {
    return (
      <div role="status" style={{ padding: 24, maxWidth: 480, margin: "48px auto" }}>
        <h2 style={{ marginTop: 0 }}>Não foi possível confirmar o plano</h2>
        <p style={{ color: "#555", lineHeight: 1.5 }}>
          Tente novamente em instantes. O acesso não foi alterado.
        </p>
      </div>
    );
  }

  return (
    <div role="status" style={{ padding: 24, maxWidth: 480, margin: "48px auto" }}>
      <h2 style={{ marginTop: 0 }}>Recurso do plano Gestão</h2>
      <p style={{ color: "#555", lineHeight: 1.5 }}>
        O financeiro da casa está disponível a partir do plano Gestão. Faça
        upgrade para liberar contas, contribuições, gastos e mensalidades.
      </p>
      {canViewPlans ? (
        <button
          type="button"
          onClick={() => navigate(ROUTE.SETTINGS_PLAN)}
          style={{
            border: "none",
            borderRadius: 8,
            padding: "10px 16px",
            background: "#111827",
            color: "#fff",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Ver planos
        </button>
      ) : null}
    </div>
  );
};

const PermissionProtectedPage: React.FC<PermissionProtectedPageProps> = ({
  requiredPermissions,
  mode = "any",
  children,
  allowWithoutTerreiro = false,
  allowWaitingApproval = false,
  requiredFeature,
}) => {
  const cachedTerreiro = useCachedTerreiro();
  const terreiroId = cachedTerreiro?.id;
  const cachedMedium = useCachedMedium();
  const mediumId = cachedMedium?.id;
  const { hasFeature, featuresLoading, featuresError } = useSubscription();
  const canViewPlans = useHasPermission(
    terreiroId,
    "settings.manage_subscription",
  );

  const permissionList = useMemo(
    () =>
      Array.isArray(requiredPermissions)
        ? requiredPermissions.filter(Boolean)
        : [requiredPermissions].filter(Boolean),
    [requiredPermissions],
  );

  const { data: permissions, isLoading } = useMyPermissionsQuery({
    terreiroId,
    enabled: !!terreiroId,
  });

  const { data: mediumStatusData, isLoading: isMediumStatusLoading } =
    useMediumStatusQuery({
      mediumId,
      enabled: !!terreiroId && !!mediumId,
    });

  if (!terreiroId) {
    if (!allowWithoutTerreiro) {
      return <Forbidden />;
    }

    return (
      <ModuleErrorBoundary>
        <Suspense fallback={<Loading />}>{children}</Suspense>
      </ModuleErrorBoundary>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isMediumStatusLoading) {
    return <Loading />;
  }

  const mediumStatus =
    typeof mediumStatusData === "object" &&
    mediumStatusData !== null &&
    "status" in mediumStatusData
      ? String(
          (mediumStatusData as { status?: unknown }).status ?? "",
        ).toUpperCase()
      : "";

  if (mediumStatus === "WAITING_APPROVAL") {
    if (!allowWaitingApproval) {
      return <PendingApprovalBlocked />;
    }

    return (
      <ModuleErrorBoundary>
        <Suspense fallback={<Loading />}>{children}</Suspense>
      </ModuleErrorBoundary>
    );
  }

  const grantedPermissions = permissions ?? [];
  const hasFullAccess = grantedPermissions.includes("admin.full_access");

  const hasRequiredPermission =
    hasFullAccess ||
    (mode === "all"
      ? permissionList.every((permission) =>
          grantedPermissions.includes(permission),
        )
      : permissionList.some((permission) =>
          grantedPermissions.includes(permission),
        ));

  if (!hasRequiredPermission) {
    return <Forbidden />;
  }

  if (requiredFeature && terreiroId) {
    if (featuresLoading) {
      return <Loading />;
    }
    if (featuresError || !hasFeature(requiredFeature)) {
      return (
        <PlanFeatureBlocked
          canViewPlans={canViewPlans}
          unavailable={featuresError}
        />
      );
    }
  }

  return (
    <ModuleErrorBoundary>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </ModuleErrorBoundary>
  );
};

export default PermissionProtectedPage;
